// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { ButtonIcon } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CaretRight from "@pacific/XDL/Icons/CaretRight";
import { headingsFontConfig } from "@pacific/XDL/PreStyle/typography";
import { color, xScreen } from "@pacific/XDL/Utilities";
import { mergeWithCss } from "@pacific/XDL/Utilities/mergeWithCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    text?: string;
    onClickHandler?: () => void;
    link?: LinkFieldValue;
    extendedStyles?: ViewMoreCategoriesCardStyles;
}

type Props = OwnProps;

export interface ViewMoreCategoriesCardStyles {
    wrapper?: ClickablePresentationProps;
    textWrapper?: InjectableCss;
    text?: TypographyPresentationProps;
    arrowWrapper?: InjectableCss;
    arrow?: InjectableCss;
}

export const viewMoreCategoriesCardStyles: ViewMoreCategoriesCardStyles = {
    wrapper: {
        css: css`
            width: 198px;
            height: 198px;
            background-color: ${color("common.ui1")};
            border: 1px solid ${color("primary.main")};
            padding: 9px;
            position: relative;
            ${xScreen("lte", "md")} {
                width: 166px;
                height: 166px;
            }
            ${xScreen("lte", "sm")} {
                padding: 7px;
                width: 152px;
                height: 152px;
            }
        `,
    },
    textWrapper: {
        css: css`
            background-color: ${color("primary.main", 0.1)};
            width: 100%;
            height: 100%;
            display: flex;
            padding: 15px;
            align-items: center;
            justify-content: center;
        `,
    },
    text: {
        size: 15,
        weight: 600,
        lineHeight: "21px",
        transform: "capitalize",
        css: css`
            letter-spacing: 0;
            text-align: center;
            color: ${color("primary.main")};
            &:hover {
                color: ${color("common.ui8")};
            }
        `,
    },
    arrowWrapper: {
        css: css`
            position: absolute;
            right: 0;
            bottom: 0;
            border: 1px solid ${color("primary.main")};
            border-width: 1px 0 0 1px;
            background-color: ${color("common.ui1")};
            display: flex;
            justify-content: center;
            align-items: center;
            height: 44px;
            width: 44px;
            ${xScreen("lte", "md")} {
                height: 52px;
                width: 52px;
            }
            ${xScreen("lte", "sm")} {
                height: 34px;
                width: 34px;
            }
            & > ${IconWrapper} {
                display: flex;
                justify-content: center;
                color: ${color("primary.main")};
                svg {
                    height: 24px;
                    width: 12px;
                }
            }
            &:hover {
                & > ${IconWrapper} {
                    color: ${color("common.ui8")};
                }
            }
        `,
    },
};

const ViewMoreCategoriesCard: FC<Props> = ({ text, link, onClickHandler, extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(viewMoreCategoriesCardStyles, extendedStyles));
    const url = link ? useGetLink(link ?? { type: "Page", value: "" }).url : undefined;
    return (
        <Clickable href={url} {...styles.wrapper} onClick={() => onClickHandler?.()} aria-label={`view more ${text}`}>
            <StyledWrapper {...styles.textWrapper}>
                <Typography {...styles.text}>{text}</Typography>
                <StyledWrapper {...styles.arrowWrapper}>
                    <ButtonIcon src={CaretRight} color="primary.main" onClick={() => onClickHandler?.()} />
                </StyledWrapper>
            </StyledWrapper>
        </Clickable>
    );
};

export default ViewMoreCategoriesCard;
